import type { PortalPopulationReferenceResponse } from "@/contracts/portal";
import { termCodeFromId } from "@/lib/report-data";
import { HIGH_BAND_MAX_INDEX, MODERATE_BAND_MAX_INDEX } from "@/lib/score-bands";
import type { TermId, YearLevel } from "@/types";

export interface PopulationReferenceBandCutoffs {
  veryLowMax: number;
  lowMax: number;
  source: "payload-explicit" | "payload-derived-bins" | "normal-fallback";
}

interface ReferenceBin {
  midpoint?: number;
  mid?: number;
  weight?: number;
  count?: number;
}

interface ReferenceBandCutoffPayload {
  veryLowMaxStandardScore?: number;
  lowMaxStandardScore?: number;
}

interface ReferenceDistributionLike {
  yearLevel?: YearLevel;
  term?: string;
  bins?: ReferenceBin[];
  bandCutoffs?: ReferenceBandCutoffPayload;
}

const FALLBACK_CUTOFFS: PopulationReferenceBandCutoffs = {
  veryLowMax: HIGH_BAND_MAX_INDEX,
  lowMax: MODERATE_BAND_MAX_INDEX,
  source: "normal-fallback",
};

function isValidCutoffPair(cutoffs: ReferenceBandCutoffPayload | null | undefined): cutoffs is Required<ReferenceBandCutoffPayload> {
  return Number.isFinite(cutoffs?.veryLowMaxStandardScore) &&
    Number.isFinite(cutoffs?.lowMaxStandardScore) &&
    Number(cutoffs!.veryLowMaxStandardScore) < Number(cutoffs!.lowMaxStandardScore);
}

function cutoffFromBins(bins: ReferenceBin[] | undefined, percentile: number): number | null {
  const normalised = (bins ?? [])
    .map((bin) => ({
      midpoint: Number(bin.midpoint ?? bin.mid),
      weight: Math.max(0, Number(bin.weight ?? bin.count ?? 0)),
    }))
    .filter((bin) => Number.isFinite(bin.midpoint) && bin.weight > 0)
    .sort((a, b) => a.midpoint - b.midpoint);

  const total = normalised.reduce((sum, bin) => sum + bin.weight, 0);
  if (total <= 0) return null;

  const target = total * (percentile / 100);
  let cumulative = 0;
  for (const bin of normalised) {
    const previous = cumulative;
    cumulative += bin.weight;
    if (cumulative >= target) {
      const binWidth = 5;
      const fraction = bin.weight > 0 ? (target - previous) / bin.weight : 0;
      const lower = bin.midpoint - binWidth / 2;
      return lower + Math.max(0, Math.min(1, fraction)) * binWidth;
    }
  }
  return normalised.at(-1)?.midpoint ?? null;
}

function resolveDistribution(
  reference: PortalPopulationReferenceResponse | null | undefined,
  year: YearLevel,
  term: TermId | string,
): ReferenceDistributionLike | null {
  const distributions = reference?.distributions;
  if (!distributions) return null;

  /* Match term ids in both spellings: full ids ("2026-T2") and compact codes
     ("T1"). termCodeFromId maps full ids onto the T1/T3/T4 window codes. */
  const termId = String(term);
  const termCode = termCodeFromId(termId);
  const termMatches = (candidate: string | undefined) =>
    candidate === termId || candidate === termCode || (candidate != null && termCodeFromId(candidate) === termCode);

  if (Array.isArray(distributions)) {
    return distributions.find((candidate) => candidate.yearLevel === year && termMatches(candidate.term)) ??
      distributions.find((candidate) => candidate.yearLevel === year && !candidate.term) ??
      distributions.find((candidate) => candidate.yearLevel === year) ??
      null;
  }

  if (typeof distributions === "object") {
    const objectDistributions = distributions as Record<string, ReferenceDistributionLike | Record<string, ReferenceDistributionLike> | undefined>;
    const byTerm = (objectDistributions[termId] ?? objectDistributions[termCode]) as Record<string, ReferenceDistributionLike> | undefined;
    const termYearDistribution = byTerm?.[year];
    if (termYearDistribution) return termYearDistribution;
    const yearDistribution = objectDistributions[year] as ReferenceDistributionLike | undefined;
    return yearDistribution ?? null;
  }

  return null;
}

export function derivePopulationReferenceBandCutoffsFromBins(
  distribution: ReferenceDistributionLike | null | undefined,
): PopulationReferenceBandCutoffs | null {
  const veryLowMax = cutoffFromBins(distribution?.bins, 15);
  const lowMax = cutoffFromBins(distribution?.bins, 35);
  if (!Number.isFinite(veryLowMax) || !Number.isFinite(lowMax) || veryLowMax == null || lowMax == null || veryLowMax >= lowMax) {
    return null;
  }
  return { veryLowMax, lowMax, source: "payload-derived-bins" };
}

export function getPopulationReferenceBandCutoffs(args: {
  populationReference: PortalPopulationReferenceResponse | null | undefined;
  year: YearLevel;
  term: TermId | string;
}): PopulationReferenceBandCutoffs {
  const distribution = resolveDistribution(args.populationReference, args.year, args.term);

  if (isValidCutoffPair(distribution?.bandCutoffs)) {
    return {
      veryLowMax: Number(distribution.bandCutoffs.veryLowMaxStandardScore),
      lowMax: Number(distribution.bandCutoffs.lowMaxStandardScore),
      source: "payload-explicit",
    };
  }

  return derivePopulationReferenceBandCutoffsFromBins(distribution) ?? FALLBACK_CUTOFFS;
}
